// Tutorial context provider - manages tutorial step progression and overlay state
// Note: gameState is loaded into the real gameStore, not managed here
import React, { createContext, useContext, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { TUTORIAL_STEPS, PHASE_CHECKPOINTS } from '../../data/tutorialStepConfig'
import type { TutorialStep, TutorialPhase } from '../../data/tutorialStepConfig'

interface TutorialContextValue {
  // Current state
  currentStep: number
  totalSteps: number
  currentStepData: TutorialStep
  selectedCardId: string | null

  // Navigation
  nextStep: () => void
  prevStep: () => void
  skipToPhase: (phase: TutorialPhase) => void
  exitTutorial: () => void

  // Derived state
  canGoPrev: boolean
  canGoNext: boolean
  isAnimating: boolean
}

const TutorialContext = createContext<TutorialContextValue | null>(null)

export function useTutorial() {
  const context = useContext(TutorialContext)
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider')
  }
  return context
}

/**
 * Hook for components that are used in both tutorial and main game contexts.
 * Returns null when TutorialProvider is not present (main game route).
 *
 * Use this in shared components like PlayerHand, ArtistBoard, etc.
 *
 * @example
 * const tutorial = useTutorialOptional()
 * const highlights = tutorial?.currentStepData.highlights ?? []
 */
export function useTutorialOptional(): TutorialContextValue | null {
  return useContext(TutorialContext)
}

interface TutorialProviderProps {
  children: React.ReactNode
}

/**
 * Tutorial provider manages step progression and overlay state.
 * The actual game state is loaded into the real gameStore by TutorialPage.
 * This keeps the tutorial lightweight - just "predetermined state + overlay".
 */
export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [isAnimating, setIsAnimating] = useState(false)

  const totalSteps = TUTORIAL_STEPS.length
  const currentStepIndex = currentStep - 1
  const currentStepData = TUTORIAL_STEPS[currentStepIndex]
  const selectedCardId = currentStepData.selectedCardId ?? null

  const canGoPrev = currentStep > 1
  const canGoNext = currentStep < totalSteps

  const nextStep = useCallback(() => {
    if (currentStep >= totalSteps) {
      // Tutorial complete - navigate back to menu
      navigate('/')
      return
    }

    setIsAnimating(true)
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps))

    // Handle auto-advance delay (for future opponent action steps)
    const nextStepData = TUTORIAL_STEPS[currentStep]
    if (nextStepData?.autoAdvanceDelay) {
      setTimeout(() => {
        setCurrentStep((prev) => Math.min(prev + 1, totalSteps))
        setIsAnimating(false)
      }, nextStepData.autoAdvanceDelay)
    } else {
      setTimeout(() => setIsAnimating(false), 300)
    }
  }, [currentStep, totalSteps, navigate])

  const prevStep = useCallback(() => {
    if (currentStep <= 1) return
    setIsAnimating(true)
    setCurrentStep((prev) => Math.max(prev - 1, 1))
    setTimeout(() => setIsAnimating(false), 300)
  }, [currentStep])

  const skipToPhase = useCallback((phase: TutorialPhase) => {
    const targetStep = PHASE_CHECKPOINTS[phase]
    if (targetStep && targetStep <= totalSteps) {
      setIsAnimating(true)
      setCurrentStep(targetStep)
      setTimeout(() => setIsAnimating(false), 300)
    }
  }, [totalSteps])

  const exitTutorial = useCallback(() => {
    navigate('/')
  }, [navigate])

  const value: TutorialContextValue = {
    currentStep,
    totalSteps,
    currentStepData,
    selectedCardId,
    nextStep,
    prevStep,
    skipToPhase,
    exitTutorial,
    canGoPrev,
    canGoNext,
    isAnimating,
  }

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  )
}
